import { c as _c } from "react/compiler-runtime";
import { captureMessage } from '@sentry/browser';
import { nanoid } from 'nanoid';
import { showNotification } from 'platform/components';
import { isNil, map, move, pipe } from 'ramda';
import { isNilOrEmpty } from 'ramda-adjunct';
import { useHttpCalls } from '../api/useHttpCalls';
import { selectActivePreset, selectActivePresetDataQuery, selectCustomPresets, selectGridCode, selectIsPresetChanged } from '../store/createEagGridStore';
import { useEagGridSelector } from '../store/useEagGridSelector';
import { useEagGridStore } from '../store/useEagGridStore';
import { ActivePreset, ColumnsSetting, FeGridSettings, GetDataQueryResponse, Preset, PresetDetail, PostPresetRequest, PutPresetRenameRequest, PutPresetRequest, VirtualPreset } from '../types/Api';
import { filterTechnicalArray } from '../utils/filterTechnicalArray';
import { isPresetColumnsSettingsInvalid } from '../utils/isPresetColumnsSettingsInvalid';
import { isPresetCustom } from '../utils/isPresetCustom';
import { getSearchParamsByGridCode } from '../utils/url/getSearchParamsByGridCode';
import { setSearchParamsByGridCode } from '../utils/url/setSearchParamsByGridCode';
export type UsePresetStateReturn = {
  presets: Preset[];
  activePreset: ActivePreset;
  activePresetDataQuery: GetDataQueryResponse | null;
  setActivePreset: (preset: ActivePreset) => void;
  activatePreset: (preset: Preset) => Promise<void>;
  deactivatePreset: () => Promise<void>;
  createPreset: (title: string, description: string, currentColumnSettings: ColumnsSetting[], currentSettings: FeGridSettings) => Promise<void>;
  updatePreset: (presetId: string, currentColumnSettings: ColumnsSetting[], currentSettings: FeGridSettings) => Promise<void>;
  deletePreset: (presetId: string) => Promise<void>;
  duplicatePreset: (presetId: string) => Promise<void>;
  renamePreset: (presetId: string, title: string, description: string) => Promise<void>;
  updatePresetPosition: (fromIdx: number, toIdx: number) => Promise<void>;
  updateVirtualPreset: (virtualPreset: VirtualPreset) => Promise<void>;
  isPresetChanged: boolean;
  setIsPresetChanged: (changed: boolean) => void;
  resetOverrides: () => Promise<void>;
  rowCountGetter: (dataQueryId: string) => Promise<number>;
};
export const usePresetState = () => {
  const $ = _c(62);
  const httpCalls = useHttpCalls();
  const store = useEagGridStore();
  const gridCode = useEagGridSelector(selectGridCode);
  const activePreset = useEagGridSelector(selectActivePreset);
  const customPresets = useEagGridSelector(selectCustomPresets) ?? [];
  const activePresetDataQuery = useEagGridSelector(selectActivePresetDataQuery) ?? null;
  const isPresetChanged = useEagGridSelector(selectIsPresetChanged);
  let t0;
  if ($[0] !== httpCalls || $[1] !== store) {
    t0 = () => httpCalls.getPresets().then(response => {
      if (response) {
        store.setCustomPresets(response);
      }
      return response;
    });
    $[0] = httpCalls;
    $[1] = store;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const refreshPresets = t0;
  let t1;
  if ($[3] !== gridCode || $[4] !== httpCalls || $[5] !== store) {
    t1 = async preset => {
      const presetDetail = await httpCalls.getPreset(preset.id);
      const dataQuery = await httpCalls.getDataQuery(preset.dataQueryId);
      if (!presetDetail || !dataQuery) {
        return;
      }
      setSearchParamsByGridCode(gridCode, preset.dataQueryId);
      store.setActivePreset(presetDetail);
      store.setActivePresetDataQuery(dataQuery);
      await httpCalls.activatePreset(preset.id).then(() => {
        store.setDataQuery(dataQuery);
        store.setActivePreset(presetDetail);
        setSearchParamsByGridCode(gridCode, preset.dataQueryId);
        store.setIsPresetChanged(false);
      });
    };
    $[3] = gridCode;
    $[4] = httpCalls;
    $[5] = store;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const activatePreset = t1;
  let t2;
  if ($[7] !== gridCode || $[8] !== httpCalls || $[9] !== store) {
    t2 = async () => {
      const newVirtualPreset = await httpCalls.getVirtualPreset();
      if (!newVirtualPreset) {
        return;
      }
      const dataQuery_0 = await httpCalls.getDataQuery(newVirtualPreset.dataQueryId);
      if (!dataQuery_0) {
        return;
      }
      setSearchParamsByGridCode(gridCode, newVirtualPreset.dataQueryId);
      const virtualPresetAsActive = {
        columnsSettings: newVirtualPreset.columnsSettings,
        dataQueryId: newVirtualPreset.dataQueryId,
        gridSettings: newVirtualPreset.gridSettings as VirtualPreset['gridSettings']
      };
      store.setActivePreset(virtualPresetAsActive);
      store.setActivePresetDataQuery(dataQuery_0);
      await httpCalls.activatePreset(null).then(() => {
        store.setDataQuery(dataQuery_0);
        store.setActivePreset(virtualPresetAsActive);
        setSearchParamsByGridCode(gridCode, newVirtualPreset.dataQueryId);
        store.setActivePresetDataQuery(dataQuery_0);
        store.setIsPresetChanged(false);
      });
    };
    $[7] = gridCode;
    $[8] = httpCalls;
    $[9] = store;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const deactivatePreset = t2;
  let t3;
  if ($[11] !== activatePreset || $[12] !== gridCode || $[13] !== httpCalls || $[14] !== store) {
    t3 = async (title, description, currentColumnSettings, currentSettings) => {
      const dataQueryId = getSearchParamsByGridCode(gridCode)?.queryId;
      const preset_0 = {
        id: nanoid(),
        title,
        description,
        dataQueryId: dataQueryId ?? "",
        gridSettings: currentSettings,
        columnsSettings: currentColumnSettings,
        isActive: true
      };
      await httpCalls.createPreset(preset_0).then(async newPreset => {
        const response_0 = await httpCalls.getPresets();
        if (!response_0) {
          return;
        }
        store.setCustomPresets(response_0);
        const createdPreset = response_0.find(res => res.id === newPreset?.presetId);
        if (isNil(createdPreset)) {
          showNotification.error("Preset was not found. Unable to activate");
          captureMessage("createPreset", scope => scope.setLevel("error").setExtras({
            userDescription: "create custom preset from usePresetState - createPreset",
            state: createdPreset
          }));
          return;
        }
        await activatePreset(createdPreset);
      });
    };
    $[11] = activatePreset;
    $[12] = gridCode;
    $[13] = httpCalls;
    $[14] = store;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const createPreset = t3;
  let t4;
  if ($[16] !== gridCode || $[17] !== httpCalls || $[18] !== refreshPresets || $[19] !== store) {
    t4 = async (presetId, currentColumnSettings_0, currentSettings_0) => {
      const modifiedPreset = {
        gridSettings: currentSettings_0,
        columnsSettings: currentColumnSettings_0,
        dataQueryId: getSearchParamsByGridCode(gridCode)?.queryId ?? ""
      };
      if (isPresetColumnsSettingsInvalid(modifiedPreset)) {
        captureMessage("updatePreset", scope_0 => scope_0.setLevel("error").setExtras({
          userDescription: "update custom preset from usePresetState - updatePreset",
          state: modifiedPreset
        }));
        return;
      }
      await httpCalls.updatePreset(presetId, modifiedPreset).then(() => {
        const prev = store.getState().activePreset;
        store.setActivePreset({
          ...(prev ?? {}),
          ...modifiedPreset
        } as ActivePreset);
        store.setIsPresetChanged(false);
      });
      await refreshPresets();
    };
    $[16] = gridCode;
    $[17] = httpCalls;
    $[18] = refreshPresets;
    $[19] = store;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  const updatePreset = t4;
  let t5;
  if ($[21] !== activePreset || $[22] !== deactivatePreset || $[23] !== httpCalls || $[24] !== refreshPresets) {
    t5 = async presetId_0 => {
      const shouldDeactivatePreset = isPresetCustom(activePreset) && activePreset.id === presetId_0;
      await httpCalls.deletePreset(presetId_0);
      if (shouldDeactivatePreset) {
        await deactivatePreset();
      }
      await refreshPresets();
    };
    $[21] = activePreset;
    $[22] = deactivatePreset;
    $[23] = httpCalls;
    $[24] = refreshPresets;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  const deletePreset = t5;
  let t6;
  if ($[26] !== httpCalls || $[27] !== refreshPresets) {
    t6 = async presetId_1 => {
      const presetDetail_0 = await httpCalls.getPreset(presetId_1);
      if (!presetDetail_0) {
        return;
      }
      const preset_1 = {
        ...presetDetail_0,
        id: nanoid(),
        title: `${presetDetail_0.title} (copy)`
      };
      await httpCalls.createPreset(preset_1);
      await refreshPresets();
    };
    $[26] = httpCalls;
    $[27] = refreshPresets;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  const duplicatePreset = t6;
  let t7;
  if ($[29] !== httpCalls || $[30] !== refreshPresets) {
    t7 = async (presetId_2, title_0, description_0) => {
      const renamedPreset = {
        title: title_0,
        description: description_0
      };
      await httpCalls.updateRenamePreset(presetId_2, renamedPreset);
      await refreshPresets();
    };
    $[29] = httpCalls;
    $[30] = refreshPresets;
    $[31] = t7;
  } else {
    t7 = $[31];
  }
  const renamePreset = t7;
  let t8;
  if ($[32] !== customPresets || $[33] !== httpCalls || $[34] !== refreshPresets) {
    t8 = async (fromIdx, toIdx) => {
      const newCustomPresetsOrder = pipe(map(_temp), move(fromIdx)(toIdx))(customPresets);
      await httpCalls.presetOrder(newCustomPresetsOrder);
      await refreshPresets();
    };
    $[32] = customPresets;
    $[33] = httpCalls;
    $[34] = refreshPresets;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  const updatePresetPosition = t8;
  let t9;
  if ($[36] !== gridCode || $[37] !== httpCalls) {
    t9 = async virtualPreset => {
      const searchParams = getSearchParamsByGridCode(gridCode);
      if (isNilOrEmpty(searchParams)) {
        return;
      }
      const dataQueryId_0 = searchParams?.queryId;
      if (isNilOrEmpty(dataQueryId_0)) {
        return;
      }
      const modifiedPreset_0 = {
        ...virtualPreset,
        columnsSettings: filterTechnicalArray(virtualPreset.columnsSettings),
        dataQueryId: dataQueryId_0 ?? ""
      };
      if (isPresetColumnsSettingsInvalid(modifiedPreset_0)) {
        captureMessage("updatePreset", scope_1 => scope_1.setLevel("error").setExtras({
          userDescription: "update virtual preset from usePresetState - updateVirtualPreset",
          state: modifiedPreset_0
        }));
        return;
      }
      await httpCalls.updateVirtualPreset(modifiedPreset_0);
    };
    $[36] = gridCode;
    $[37] = httpCalls;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  const updateVirtualPreset = t9;
  let t10;
  if ($[39] !== activatePreset || $[40] !== activePreset) {
    t10 = async () => {
      if (!isPresetCustom(activePreset)) {
        return;
      }
      await activatePreset(activePreset as Preset);
    };
    $[39] = activatePreset;
    $[40] = activePreset;
    $[41] = t10;
  } else {
    t10 = $[41];
  }
  const resetOverrides = t10;
  let t11;
  if ($[42] !== httpCalls) {
    t11 = dataQueryId_1 => httpCalls.getCountByQuery(dataQueryId_1).then(_temp2);
    $[42] = httpCalls;
    $[43] = t11;
  } else {
    t11 = $[43];
  }
  const rowCountGetter = t11;
  let t12;
  if ($[44] !== activatePreset || $[45] !== activePreset || $[46] !== activePresetDataQuery || $[47] !== createPreset || $[48] !== customPresets || $[49] !== deactivatePreset || $[50] !== deletePreset || $[51] !== duplicatePreset || $[52] !== isPresetChanged || $[53] !== renamePreset || $[54] !== resetOverrides || $[55] !== rowCountGetter || $[56] !== store.setActivePreset || $[57] !== store.setIsPresetChanged || $[58] !== updatePreset || $[59] !== updatePresetPosition || $[60] !== updateVirtualPreset) {
    t12 = {
      presets: customPresets,
      activePreset,
      activePresetDataQuery,
      setActivePreset: store.setActivePreset,
      activatePreset,
      deactivatePreset,
      createPreset,
      updatePreset,
      deletePreset,
      duplicatePreset,
      renamePreset,
      updatePresetPosition,
      updateVirtualPreset,
      isPresetChanged,
      setIsPresetChanged: store.setIsPresetChanged,
      resetOverrides,
      rowCountGetter
    };
    $[44] = activatePreset;
    $[45] = activePreset;
    $[46] = activePresetDataQuery;
    $[47] = createPreset;
    $[48] = customPresets;
    $[49] = deactivatePreset;
    $[50] = deletePreset;
    $[51] = duplicatePreset;
    $[52] = isPresetChanged;
    $[53] = renamePreset;
    $[54] = resetOverrides;
    $[55] = rowCountGetter;
    $[56] = store.setActivePreset;
    $[57] = store.setIsPresetChanged;
    $[58] = updatePreset;
    $[59] = updatePresetPosition;
    $[60] = updateVirtualPreset;
    $[61] = t12;
  } else {
    t12 = $[61];
  }
  return t12;
};
function _temp(preset_2) {
  return preset_2.id;
}
function _temp2(response_1) {
  return response_1 ?? 0;
}